<template>
  <div
    v-show="visible"
    class="vuepal-command-palette"
    @wheel.stop
    @click.stop
    @keydown="onKeyDown"
  >
    <div class="vuepal-command-palette-input">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path
          d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
        />
      </svg>
      <input
        ref="inputEl"
        v-model="text"
        type="text"
        placeholder="Search menu..."
      />
    </div>
    <div class="vuepal-command-palette-results vuepal-scrollbar-dark">
      <button
        v-for="(match, index) in matches"
        :key="match.item.id"
        class="vuepal-command-palette-item"
        :class="{ 'vp-is-focused': index === focusedIndex }"
        :data-vp-palette-item="index"
        @mouseenter="focusedIndex = index"
      >
        <Highlight :text="match.item.breadcrumb" :positions="match.positions" />
      </button>
    </div>
  </div>
</template>

<script setup>
import Highlight from "./Highlight.vue";
import { computed, onMounted, ref, watch, nextTick } from "#imports";
import { Fzf } from "fzf";
const props = defineProps({
  links: { type: Array, required: true },
  visible: { type: Boolean, required: true }
});
const emit = defineEmits(["close"]);
const modulo = (n, m) => (n % m + m) % m;
const focusedIndex = ref(0);
const inputEl = ref(null);
watch(
  () => props.visible,
  (isVisible) => {
    nextTick(() => {
      if (isVisible && inputEl.value) {
        inputEl.value.focus();
        inputEl.value.select();
      }
    });
  }
);
const focusPrev = () => {
  if (!matches.value?.length) {
    return;
  }
  focusedIndex.value = modulo(focusedIndex.value - 1, matches.value.length);
  scrollFocusedIntoView();
};
const focusNext = () => {
  if (!matches.value?.length) {
    return;
  }
  focusedIndex.value = modulo(focusedIndex.value + 1, matches.value.length);
  scrollFocusedIntoView();
};
const scrollFocusedIntoView = () => {
  const element = document.querySelector(
    `[data-vp-palette-item="${focusedIndex.value}"]`
  );
  if (element) {
    element.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
};
const onSelect = () => {
  const item = matches.value[focusedIndex.value];
  if (!item) {
    return;
  }
  window.location.href = item.item.url;
};
const onKeyDown = (e) => {
  const stop = () => {
    e.preventDefault();
    e.stopPropagation();
  };
  if (e.code === "Tab") {
    e.shiftKey ? focusPrev() : focusNext();
    stop();
  } else if (e.code === "ArrowDown") {
    stop();
    focusNext();
  } else if (e.code === "ArrowUp") {
    stop();
    focusPrev();
  } else if (e.code === "Enter") {
    onSelect();
    stop();
  } else if (e.code === "Escape") {
    emit("close");
    stop();
  }
};
let counter = 0;
function flattenAdminMenuLinks(links, breadcrumb = []) {
  let flattenedLinks = [];
  links.forEach((link) => {
    if (link.link?.label && link.link.url?.path) {
      const newBreadcrumb = [...breadcrumb, link.link.label];
      const breadcrumbStr = newBreadcrumb.join(" \xBB ");
      if (link.link.url?.path) {
        counter++;
        flattenedLinks.push({
          id: counter.toString(),
          url: link.link.url.path,
          label: link.link.label,
          breadcrumb: breadcrumbStr
        });
      }
      if (link.subtree && link.subtree.length > 0) {
        flattenedLinks = flattenedLinks.concat(
          flattenAdminMenuLinks(link.subtree, newBreadcrumb)
        );
      }
    }
  });
  return flattenedLinks;
}
const items = computed(() => flattenAdminMenuLinks(props.links));
const fzf = new Fzf(items.value, {
  selector: (item) => item.breadcrumb
});
const text = ref("");
watch(text, () => {
  focusedIndex.value = 0;
});
const matches = computed(() => {
  if (!text.value) {
    return [];
  }
  const results = fzf.find(text.value);
  return results.slice(0, 30).map((v) => {
    return {
      item: v.item,
      positions: [...v.positions]
    };
  });
});
onMounted(() => {
  if (inputEl.value) {
    inputEl.value.focus();
  }
});
</script>
